import { Divider, Grid, TextField } from '@material-ui/core';
import CircularProgress from '@material-ui/core/CircularProgress';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useProjectTemplateSettings } from '~/modules/common/hooks/project/useProjectTemplateSettings';
import { TimeAndExpenseEntryTypeDropdown } from '~/modules/tasks/components';
import RoleDropdown from '~/modules/projects/tasks/components/RoleDropdown';
import { getError, hasError } from '~/util';
import { TASK_ESTIMATE_CALCULATION_TYPES } from '~/modules/common/enums';
import { useIsBreakpointDown } from '~/modules/common/hooks';
import { DateField, Hours, Money2 } from '~/modules/common/components';
import TaskNameField from './TaskNameField';
import useStyles from './useStyles';
import { TaskResourceEstimates } from './components';
import { GroupTimesheetAccessDropdown } from './GroupTimesheetAccessDropdown';

const getAriaLabel = ariaLabel => ({ 'aria-label': ariaLabel });

export const AddTaskForm2 = ({
  me,
  readOnly,
  isLoading,
  project,
  projectSlug,
  values,
  errors,
  onNameChange,
  onCodeChange,
  onStartDateChange,
  onEndDateChange,
  onAssignedRoleChange,
  onDescriptionChange,
  onEstimatedHoursChange,
  onEstimatedCostAmountChange,
  onEstimatedCostCurrencyChange,
  onTimeAndExpenseEntryTypeChange,
  onResourceEstimateAdd,
  setFieldValue,
  onTimesheetAccessChange
}) => {
  const intl = useIntl();
  const classes = useStyles();
  const isMobile = useIsBreakpointDown('xs');
  const {
    featureFlags: { PSAPRPTaskEstimateCalculation, isPsaPrpPsaPpmMergerEnabled }
  } = me;

  const isTaskAssignmentsEnabled =
    PSAPRPTaskEstimateCalculation ===
      TASK_ESTIMATE_CALCULATION_TYPES.TASKASSIGNMENT && !isMobile;

  const {
    name,
    code,
    startDate,
    endDate,
    assignedRole = null,
    description,
    estimatedHours,
    estimatedCost,
    timeAndExpenseEntryType,
    resourceEstimates,
    timesheetAccess
  } = values;

  const { isExpenseProductEnabled, hasViewProjectBillingOptions } = me;

  const descriptionFieldProps = useMemo(
    () => ({
      'aria-label': intl.formatMessage({ id: 'projectTasksPage.description' })
    }),
    [intl]
  );
  const { permittedActionUris, billingType, projectTemplateSetting } = project;

  const templateSettings = useProjectTemplateSettings({
    projectTemplateSetting,
    isPsaPrpPsaPpmMergerEnabled
  });

  const { hasBilling } = templateSettings || {};

  if (isLoading) {
    return (
      <div className={classes.container}>
        <CircularProgress className={classes.loadingContainer} size={24} />
      </div>
    );
  }

  return (
    <>
      <Grid container>
        <Grid item xs={3} sm={3}>
          <TaskNameField
            variant="standard"
            name={name}
            nameLabel={intl.formatMessage({ id: 'projectTasksPage.name' })}
            onNameChange={onNameChange}
            readOnly={readOnly}
            errors={errors}
          />
        </Grid>
        <Grid item xs={3} sm={3}>
          <TextField
            fullWidth
            variant="standard"
            disabled={readOnly}
            value={code}
            onChange={onCodeChange}
            helperText={getError(errors, 'code')}
            label={intl.formatMessage({ id: 'projectTasksPage.taskCode' })}
            inputProps={getAriaLabel(
              intl.formatMessage({ id: 'projectTasksPage.taskCode' })
            )}
            error={hasError(errors, 'code')}
          />
        </Grid>
        <Grid item xs={3} sm={3}>
          <RoleDropdown
            variant="standard"
            value={assignedRole}
            onChange={onAssignedRoleChange}
            disabled={readOnly}
            label={intl.formatMessage({
              id: 'projectTasksPage.taskRole'
            })}
            projectSlug={projectSlug}
          />
        </Grid>
        <Grid item xs={3} sm={3}>
          <TimeAndExpenseEntryTypeDropdown
            showBillingOptions={
              isPsaPrpPsaPpmMergerEnabled
                ? hasBilling && hasViewProjectBillingOptions
                : hasViewProjectBillingOptions
            }
            value={timeAndExpenseEntryType && timeAndExpenseEntryType.id}
            onChange={onTimeAndExpenseEntryTypeChange}
            label={intl.formatMessage({
              id: isExpenseProductEnabled
                ? 'projectTasksPage.timeAndExpenseEntryAllowed'
                : 'projectTasksPage.timeEntryAllowed'
            })}
            disabled={Boolean(
              billingType?.id === 'urn:replicon:billing-type:non-billable'
            )}
          />
        </Grid>
      </Grid>

      <Grid container>
        <Grid item xs={3} sm={3}>
          <DateField
            editable
            variant="standard"
            value={startDate}
            onChange={onStartDateChange}
            label={intl.formatMessage({
              id: 'projectTasksPage.estimatedStartDate'
            })}
            ariaLabel={intl.formatMessage({
              id: 'projectTasksPage.estimatedStartDate'
            })}
            error={hasError(errors, 'startDate')}
            helperText={getError(errors, 'startDate')}
          />
        </Grid>
        <Grid item xs={3} sm={3}>
          <DateField
            editable
            variant="standard"
            value={endDate}
            onChange={onEndDateChange}
            label={intl.formatMessage({
              id: 'projectTasksPage.estimatedEndDate'
            })}
            ariaLabel={intl.formatMessage({
              id: 'projectTasksPage.estimatedEndDate'
            })}
            error={hasError(errors, 'endDate')}
            helperText={getError(errors, 'endDate')}
          />
        </Grid>
        {permittedActionUris.includes(
          'urn:replicon:project-action:edit-project-estimates'
        ) && (
          <>
            <Grid item xs={3} sm={3}>
              <Hours
                variant="standard"
                isEditible
                value={estimatedHours}
                onValueChange={onEstimatedHoursChange}
                label={intl.formatMessage({
                  id: 'projectTasksPage.estimatedHours'
                })}
                ariaLabel={intl.formatMessage({
                  id: 'projectTasksPage.estimatedHours'
                })}
                error={hasError(errors, 'estimatedHours')}
                helperText={getError(errors, 'estimatedHours')}
              />
            </Grid>
            {permittedActionUris.includes(
              'urn:replicon:project-action:edit-cost-data'
            ) && (
              <Grid item xs={3} sm={3}>
                <Money2
                  variant="standard"
                  amount={estimatedCost.amount}
                  onAmountChange={onEstimatedCostAmountChange}
                  currency={estimatedCost.currency}
                  onCurrencyChange={onEstimatedCostCurrencyChange}
                  label={intl.formatMessage({
                    id: 'projectTasksPage.estimatedCost'
                  })}
                  ariaLabel={intl.formatMessage({
                    id: 'projectTasksPage.estimatedCost'
                  })}
                />
              </Grid>
            )}
          </>
        )}
      </Grid>

      <Grid container>
        <Grid item xs={6}>
          <GroupTimesheetAccessDropdown
            projectId={project.id}
            timesheetAccess={timesheetAccess}
            onTimesheetAccessChange={onTimesheetAccessChange}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            rows={1}
            rowsMax={1}
            fullWidth
            multiline
            variant="standard"
            value={description}
            onChange={onDescriptionChange}
            inputProps={descriptionFieldProps}
            label={intl.formatMessage({
              id: 'projectTasksPage.description'
            })}
            error={hasError(errors, 'description')}
            helperText={getError(errors, 'description')}
          />
        </Grid>
      </Grid>

      {isTaskAssignmentsEnabled &&
        permittedActionUris.includes(
          'urn:replicon:project-action:edit-resource-task-allocations'
        ) && (
          <>
            <Divider className={classes.divider} />
            <TaskResourceEstimates
              taskEstimatedCost={estimatedCost}
              taskEstimatedHours={estimatedHours}
              onResourceEstimateAdd={onResourceEstimateAdd}
              resourceEstimates={resourceEstimates}
              setFieldValue={setFieldValue}
              projectSlug={projectSlug}
            />
          </>
        )}
    </>
  );
};

AddTaskForm2.propTypes = {
  me: PropTypes.object,
  readOnly: PropTypes.bool,
  isLoading: PropTypes.bool,
  values: PropTypes.object,
  project: PropTypes.object,
  projectSlug: PropTypes.string,
  errors: PropTypes.object,
  onNameChange: PropTypes.func,
  onCodeChange: PropTypes.func,
  onStartDateChange: PropTypes.func,
  onEndDateChange: PropTypes.func,
  onAssignedRoleChange: PropTypes.func,
  onDescriptionChange: PropTypes.func,
  onEstimatedHoursChange: PropTypes.func,
  onEstimatedCostAmountChange: PropTypes.func,
  onEstimatedCostCurrencyChange: PropTypes.func,
  onTimeAndExpenseEntryTypeChange: PropTypes.func,
  onTimesheetAccessChange: PropTypes.func,
  onResourceEstimateAdd: PropTypes.func,
  setFieldValue: PropTypes.func
};

export default AddTaskForm2;
